import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Mic, Check, Loader2, Sparkles, X } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getToken() {
  return document.cookie.split('; ').find(r => r.startsWith('session_token='))?.split('=')[1]
    || localStorage.getItem('session_token') || '';
}
const authHeaders = () => ({ Authorization: `Bearer ${getToken()}` });

export default function BrandVoiceTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState('');
  const [saved, setSaved] = useState(false);

  // Local state for editable fields
  const [tone, setTone] = useState('professional');
  const [traits, setTraits] = useState([]);
  const [customInstructions, setCustomInstructions] = useState('');
  const [sampleText, setSampleText] = useState('');

  useEffect(() => {
    axios.get(`${API}/brand-voice`, { headers: authHeaders() })
      .then(r => {
        setData(r.data);
        setTone(r.data.tone || 'professional');
        setTraits(r.data.traits || []);
        setCustomInstructions(r.data.custom_instructions || '');
        setSampleText(r.data.sample_text || '');
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    try {
      const res = await axios.put(`${API}/brand-voice`, {
        tone, traits, custom_instructions: customInstructions, sample_text: sampleText,
      }, { headers: authHeaders() });
      setData(res.data);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) { /* save failed */ }
    finally { setSaving(false); }
  };

  const handlePreview = async () => {
    setPreviewing(true);
    setPreview('');
    try {
      const res = await axios.post(`${API}/brand-voice/preview`, {
        tone, traits, custom_instructions: customInstructions,
      }, { headers: authHeaders() });
      setPreview(res.data.preview || '');
    } catch (e) {
      setPreview('Preview generation failed. Please try again.');
    }
    finally { setPreviewing(false); }
  };

  const toggleTrait = (trait) => {
    setTraits(prev =>
      prev.includes(trait) ? prev.filter(t => t !== trait) : prev.length < 5 ? [...prev, trait] : prev
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="voice-loading">
        <Loader2 size={24} className="animate-spin" style={{ color: '#8C7E6E' }} />
      </div>
    );
  }

  const tones = data?.available_tones || [];
  const availableTraits = data?.available_traits || [];

  return (
    <div data-testid="brand-voice-tab">
      {/* Status indicator */}
      {data?.configured && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="flex items-center gap-2 mb-6 p-3"
          style={{ backgroundColor: '#1D6B4F10', border: '1px solid #1D6B4F20' }}
          data-testid="voice-configured-badge"
        >
          <Check size={14} style={{ color: '#1D6B4F' }} />
          <span className="text-[12px] font-medium" style={{ color: '#1D6B4F' }}>Brand voice configured</span>
        </motion.div>
      )}

      {/* Tone Selection */}
      <div className="mb-8">
        <p className="text-[10px] uppercase tracking-widest mb-4" style={{ color: '#B8985A' }}>Voice Tone</p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {tones.map(t => (
            <button
              key={t.id}
              onClick={() => setTone(t.id)}
              className="text-left p-4 transition-all"
              style={{
                backgroundColor: tone === t.id ? '#2C2520' : '#F7F4EF',
                border: `1px solid ${tone === t.id ? '#2C2520' : '#DDD5C8'}`,
              }}
              data-testid={`tone-${t.id}`}
            >
              <p className="text-[13px] font-medium" style={{ color: tone === t.id ? '#F0EBE3' : '#3C3226' }}>
                {t.label}
              </p>
              <p className="text-[11px] mt-1" style={{ color: tone === t.id ? '#D4C9B8' : '#8C7E6E' }}>
                {t.description}
              </p>
            </button>
          ))}
        </div>
      </div>

      {/* Trait Selection */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <p className="text-[10px] uppercase tracking-widest" style={{ color: '#B8985A' }}>Brand Traits</p>
          <span className="text-[11px]" style={{ color: '#8C7E6E' }}>{traits.length}/5 selected</span>
        </div>
        <div className="flex flex-wrap gap-2">
          {availableTraits.map(trait => {
            const selected = traits.includes(trait);
            return (
              <button
                key={trait}
                onClick={() => toggleTrait(trait)}
                className="px-3 py-1.5 text-[12px] font-medium transition-all"
                style={{
                  backgroundColor: selected ? '#1D6B4F' : '#F7F4EF',
                  color: selected ? '#F0EBE3' : '#5C5347',
                  border: `1px solid ${selected ? '#1D6B4F' : '#DDD5C8'}`,
                }}
                data-testid={`trait-${trait.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {selected && <Check size={11} className="inline mr-1" />}
                {trait}
              </button>
            );
          })}
        </div>
      </div>

      {/* Custom Instructions */}
      <div className="mb-8">
        <p className="text-[10px] uppercase tracking-widest mb-3" style={{ color: '#B8985A' }}>Custom Instructions</p>
        <textarea
          value={customInstructions}
          onChange={e => setCustomInstructions(e.target.value)}
          placeholder="E.g., 'Always mention our 20 years of experience' or 'Keep it family-friendly'"
          maxLength={500}
          rows={3}
          className="w-full p-3 text-[13px] outline-none resize-none"
          style={{
            backgroundColor: '#F7F4EF',
            border: '1px solid #DDD5C8',
            color: '#3C3226',
          }}
          data-testid="custom-instructions-input"
        />
        <p className="text-[11px] mt-1 text-right" style={{ color: '#8C7E6E' }}>{customInstructions.length}/500</p>
      </div>

      {/* Sample Text (optional) */}
      <div className="mb-8">
        <p className="text-[10px] uppercase tracking-widest mb-3" style={{ color: '#B8985A' }}>Sample Text (optional)</p>
        <p className="text-[12px] mb-2" style={{ color: '#8C7E6E' }}>
          Paste a paragraph that represents how you'd like to sound. We'll use it to guide AI content generation.
        </p>
        <textarea
          value={sampleText}
          onChange={e => setSampleText(e.target.value)}
          placeholder="Paste a paragraph from your website, an email, or a social post..."
          maxLength={1000}
          rows={4}
          className="w-full p-3 text-[13px] outline-none resize-none"
          style={{
            backgroundColor: '#F7F4EF',
            border: '1px solid #DDD5C8',
            color: '#3C3226',
          }}
          data-testid="sample-text-input"
        />
      </div>

      {/* Action buttons */}
      <div className="flex items-center gap-3 mb-8">
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-6 py-3 text-[13px] font-medium transition-all"
          style={{ backgroundColor: '#2C2520', color: '#F0EBE3', opacity: saving ? 0.6 : 1 }}
          data-testid="save-voice-btn"
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : saved ? <Check size={14} /> : <Mic size={14} />}
          {saving ? 'Saving...' : saved ? 'Saved' : 'Save Voice Settings'}
        </button>
        <button
          onClick={handlePreview}
          disabled={previewing}
          className="flex items-center gap-2 px-6 py-3 text-[13px] font-medium transition-all"
          style={{
            backgroundColor: '#F7F4EF',
            border: '1px solid #DDD5C8',
            color: '#3C3226',
            opacity: previewing ? 0.6 : 1,
          }}
          data-testid="preview-voice-btn"
        >
          {previewing ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
          {previewing ? 'Generating...' : 'Preview Voice'}
        </button>
      </div>

      {/* Preview output */}
      {(preview || previewing) && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-5 mb-4"
          style={{ backgroundColor: '#2C2520' }}
          data-testid="voice-preview-output"
        >
          <div className="flex items-center justify-between mb-3">
            <p className="text-[10px] uppercase tracking-widest" style={{ color: '#B8985A' }}>Preview</p>
            {preview && (
              <button onClick={() => setPreview('')} className="p-1">
                <X size={14} style={{ color: '#8C7E6E' }} />
              </button>
            )}
          </div>
          {previewing ? (
            <div className="flex items-center gap-2 py-4">
              <Loader2 size={16} className="animate-spin" style={{ color: '#B8985A' }} />
              <span className="text-[13px]" style={{ color: '#D4C9B8' }}>Generating preview in your brand voice...</span>
            </div>
          ) : (
            <p className="text-[14px] leading-relaxed" style={{ color: '#F0EBE3' }}>{preview}</p>
          )}
        </motion.div>
      )}
    </div>
  );
}
